'use client';

/**
 * Career-path explorer — a row of selectable career tiles above a
 * detail panel for the active career. Ported from the BTK Akademi v2 reference
 * ({@code .career-section} / {@code .career-picker} / {@code
 * .career-detail} / {@code .roadmap}).
 *
 * <p>Interaction: click a tile → its detail panel renders below
 * (head + KPIs, a stage-pill summary, and either a winding SVG
 * "roadmap" of course nodes or a vertical stage timeline, then a CTA
 * row). The roadmap path + node coordinates are generated from the
 * course count and {@code perRow} cadence, so authors only supply the
 * ordered nodes — no hand-tuned percentages.
 *
 * <p>Per-career visual mode:
 * <ul>
 *   <li>{@code nodes} present → SVG roadmap (with the optional stage-pill
 *       banner above it);</li>
 *   <li>else {@code stages} present → vertical stage timeline.</li>
 * </ul>
 *
 * <p>Purely presentational. On the LMS portal the {@code
 * LearningPathV2Block} wrapper builds {@code careers} live from the
 * learner's assigned learning paths (one path → one career, its courses
 * → roadmap nodes with progress/lock state); standalone it renders the
 * authored careers / defaults so the admin preview still works.
 */
import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser, SidebarIcon, PortalLink } from '@akademi/site-builder-runtime';
import { tx, asList } from '../_shared/coerce';
import { SectionShell } from '../_shared/SectionShell';
import type { PaddingBox } from '../_shared/resolvePadding';
import { EmptyState } from '../_shared/EmptyState';
import { Button } from '@akademi/ui';

type Localized = string | Record<string, string>;
type NodeState = 'done' | 'current' | 'locked' | 'trophy';
type StageState = 'done' | 'current' | 'locked' | 'trophy';

interface CareerKpi {
  value?: string;
  label?: Localized;
  accent?: boolean;
}

interface RoadNode {
  /** Node label under/over the pin. */
  label?: Localized;
  state?: NodeState;
  /** Deep link — node becomes an anchor when set and not locked. */
  href?: string;
}

interface StageCourse {
  name?: Localized;
  dur?: string;
  done?: boolean;
}

interface Stage {
  eyebrow?: Localized;
  title?: Localized;
  meta?: string;
  desc?: Localized;
  state?: StageState;
  courses?: StageCourse[];
}

interface StagePill {
  label?: Localized;
  state?: StageState;
}

interface Career {
  id?: string;
  /** Path thumbnail/cover image. When set, shown in the tile + detail
   *  head instead of {@link icon}. */
  image?: string;
  /** SidebarIcon name (catalog), e.g. 'brain', 'shield', 'book-open'.
   *  Fallback when {@link image} is absent. */
  icon?: string;
  name?: Localized;
  /** Tile sub-line, e.g. "12 ders · 84 saat". */
  meta?: Localized;
  /** Corner ribbon, e.g. "Popüler" / "Yeni". */
  badge?: Localized;
  /** Accent tint (tile icon + roadmap). Falls back to the brand color. */
  tint?: string;
  /** Second tint for the detail-icon gradient. */
  tint2?: string;
  /** Detail title (defaults to {@code name}). */
  title?: Localized;
  desc?: Localized;
  kpis?: CareerKpi[];
  /** Stage-pill summary banner (shown above the roadmap). */
  stagePills?: StagePill[];
  /** Roadmap course nodes — when present, render the SVG roadmap. */
  nodes?: RoadNode[];
  /** Courses per winding row in the roadmap. Default 5. */
  perRow?: number;
  /** Vertical stage timeline — used when {@code nodes} is empty. */
  stages?: Stage[];
  /** Footer hint line next to the CTA. */
  metaFoot?: Localized;
  /** CTA button label. */
  ctaLabel?: Localized;
  /** CTA icon ('play' = resume, 'flag' = start). */
  ctaIcon?: 'play' | 'flag';
  ctaHref?: string;
}

export interface LearningPathV2Props {
  careers?: Career[];
  /** Id of the initially-open career. Defaults to the first. */
  activeId?: string;

  /* --- consumed by the portal wrapper, ignored by this block --- */
  /** Live feed selector ({@code assigned} | {@code all} | {@code custom}). */
  source?: string;
  /** Restrict the live feed to these path ids. */
  ids?: string[];

  /**
   * Auth-gate the whole block. {@code 'any'} (default) always renders;
   * {@code 'in'} only for signed-in users; {@code 'out'} only for
   * anonymous visitors.
   */
  requireAuth?: 'any' | 'in' | 'out';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

const ACCENT = 'var(--color-brand-500, #1AA5BC)';
const ACCENT2 = 'var(--color-brand-400, #5EC8D8)';
const SURFACE = 'var(--color-surface-card, #ffffff)';
const SURFACE2 = 'var(--color-surface-muted, var(--color-neutral-50, #f8fafc))';
const BORDER = 'var(--color-border-default, rgba(2,6,23,0.10))';
const BORDER_SOFT = 'var(--color-border-subtle, rgba(2,6,23,0.06))';
const TEXT = 'var(--color-text-primary, #0f172a)';
const MUTED = 'var(--color-text-secondary, #475569)';
const DIM = 'var(--color-text-tertiary, #94a3b8)';

/** The i18next translate function, as much of it as this module uses. */
type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Preview careers drawn when nothing is authored — the admin preview and
 * the standalone block both need something on screen. Built per call (not
 * a module constant) so the copy is read from the catalogue at render
 * time and follows a language switch instead of freezing the boot locale.
 */
function buildDefaultCareers(t: Translate): Career[] {
  const lessonHours = (lessons: number, hours: number) =>
    t('careerPath.demo.lessonHours', { lessons, hours });
  const months = (n: number) => t('careerPath.demo.months', { months: n });
  const shortHours = (n: number) => t('careerPath.demo.hoursShort', { hours: n });
  const stage = (n: number) => t('careerPath.demo.stage.step', { step: n });
  const certificate = t('careerPath.demo.certificate');
  const kpiProgress = t('careerPath.demo.kpi.progress');
  const kpiDuration = t('careerPath.demo.kpi.duration');
  const kpiPeers = t('careerPath.demo.kpi.peers');
  const startCta = t('careerPath.demo.cta.start');

  return [
    {
      id: 'frontend',
      icon: 'layout-grid',
      name: t('careerPath.demo.frontend.name'),
      meta: lessonHours(12, 84),
      badge: t('careerPath.demo.badge.popular'),
      tint: '#1AA5BC',
      desc: t('careerPath.demo.frontend.desc'),
      kpis: [
        { value: '5/12', label: kpiProgress, accent: true },
        { value: months(6), label: kpiDuration },
        { value: '8.4k', label: kpiPeers },
      ],
      stagePills: [
        { label: t('careerPath.demo.frontend.pill.1'), state: 'done' },
        { label: t('careerPath.demo.frontend.pill.2'), state: 'done' },
        { label: t('careerPath.demo.frontend.pill.3'), state: 'current' },
        { label: t('careerPath.demo.frontend.pill.4'), state: 'locked' },
        { label: t('careerPath.demo.frontend.pill.final'), state: 'trophy' },
      ],
      perRow: 5,
      nodes: [
        { label: t('careerPath.demo.frontend.node.html'), state: 'done' },
        { label: t('careerPath.demo.frontend.node.cssLayout'), state: 'done' },
        { label: t('careerPath.demo.frontend.node.jsIntro'), state: 'done' },
        { label: t('careerPath.demo.frontend.node.modernJs'), state: 'done' },
        { label: t('careerPath.demo.frontend.node.git'), state: 'done' },
        { label: t('careerPath.demo.frontend.node.reactBasics'), state: 'current' },
        { label: t('careerPath.demo.frontend.node.state') },
        { label: t('careerPath.demo.frontend.node.typescript') },
        { label: t('careerPath.demo.frontend.node.performance') },
        { label: t('careerPath.demo.frontend.node.testing') },
        { label: t('careerPath.demo.frontend.node.cicd') },
        { label: t('careerPath.demo.frontend.node.accessibility') },
        { label: t('careerPath.demo.frontend.node.capstone') },
        { label: certificate, state: 'trophy' },
      ],
      metaFoot: t('careerPath.demo.frontend.metaFoot'),
      ctaLabel: t('careerPath.demo.frontend.cta'),
      ctaIcon: 'play',
    },
    {
      id: 'backend',
      icon: 'layers',
      name: t('careerPath.demo.backend.name'),
      meta: lessonHours(14, 96),
      tint: '#1AA5BC',
      tint2: '#5EC8D8',
      desc: t('careerPath.demo.backend.desc'),
      kpis: [
        { value: '0/14', label: kpiProgress, accent: true },
        { value: months(7), label: kpiDuration },
        { value: '6.1k', label: kpiPeers },
      ],
      stages: [
        {
          eyebrow: t('careerPath.demo.stage.startHere'),
          title: t('careerPath.demo.backend.stage.1.title'),
          meta: lessonHours(3, 22),
          state: 'current',
          desc: t('careerPath.demo.backend.stage.1.desc'),
          courses: [
            { name: t('careerPath.demo.backend.stage.1.course.python'), dur: shortHours(9) },
            {
              name: t('careerPath.demo.backend.stage.1.course.dataStructures'),
              dur: shortHours(8),
            },
            { name: t('careerPath.demo.backend.stage.1.course.linux'), dur: shortHours(5) },
          ],
        },
        {
          eyebrow: stage(2),
          title: t('careerPath.demo.backend.stage.2.title'),
          meta: lessonHours(4, 28),
          desc: t('careerPath.demo.backend.stage.2.desc'),
        },
        {
          eyebrow: stage(3),
          title: t('careerPath.demo.backend.stage.3.title'),
          meta: lessonHours(3, 20),
          desc: t('careerPath.demo.backend.stage.3.desc'),
        },
        {
          eyebrow: stage(4),
          title: t('careerPath.demo.backend.stage.4.title'),
          meta: lessonHours(4, 26),
          desc: t('careerPath.demo.backend.stage.4.desc'),
        },
        {
          eyebrow: t('careerPath.demo.stage.final'),
          title: t('careerPath.demo.backend.stage.final.title'),
          meta: certificate,
          state: 'trophy',
        },
      ],
      metaFoot: t('careerPath.demo.backend.metaFoot'),
      ctaLabel: startCta,
      ctaIcon: 'flag',
    },
    {
      id: 'data',
      icon: 'trending-up',
      name: t('careerPath.demo.data.name'),
      meta: lessonHours(16, 110),
      tint: '#5EC8D8',
      tint2: '#1AA5BC',
      desc: t('careerPath.demo.data.desc'),
      kpis: [
        { value: '0/16', label: kpiProgress, accent: true },
        { value: months(8), label: kpiDuration },
        { value: '5.7k', label: kpiPeers },
      ],
      stages: [
        {
          eyebrow: t('careerPath.demo.stage.startHere'),
          title: t('careerPath.demo.data.stage.1.title'),
          meta: lessonHours(4, 26),
          state: 'current',
        },
        {
          eyebrow: stage(2),
          title: t('careerPath.demo.data.stage.2.title'),
          meta: lessonHours(3, 18),
        },
        {
          eyebrow: stage(3),
          title: t('careerPath.demo.data.stage.3.title'),
          meta: lessonHours(5, 36),
        },
        {
          eyebrow: stage(4),
          title: t('careerPath.demo.data.stage.4.title'),
          meta: lessonHours(3, 18),
        },
        {
          eyebrow: t('careerPath.demo.stage.final'),
          title: t('careerPath.demo.data.stage.final.title'),
          meta: certificate,
          state: 'trophy',
        },
      ],
      metaFoot: t('careerPath.demo.data.metaFoot'),
      ctaLabel: startCta,
      ctaIcon: 'flag',
    },
    {
      id: 'ai',
      icon: 'brain',
      name: t('careerPath.demo.ai.name'),
      meta: lessonHours(15, 92),
      badge: t('careerPath.demo.badge.new'),
      tint: '#1AA5BC',
      desc: t('careerPath.demo.ai.desc'),
      kpis: [
        { value: '1/15', label: kpiProgress, accent: true },
        { value: months(7), label: kpiDuration },
        { value: '4.2k', label: kpiPeers },
      ],
      stages: [
        {
          eyebrow: t('careerPath.demo.stage.doneFirst'),
          title: t('careerPath.demo.ai.stage.1.title'),
          meta: lessonHours(1, 4),
          state: 'done',
        },
        {
          eyebrow: t('careerPath.demo.stage.currentSecond'),
          title: t('careerPath.demo.ai.stage.2.title'),
          meta: lessonHours(4, 28),
          state: 'current',
        },
        {
          eyebrow: stage(3),
          title: t('careerPath.demo.ai.stage.3.title'),
          meta: lessonHours(5, 32),
        },
        {
          eyebrow: stage(4),
          title: t('careerPath.demo.ai.stage.4.title'),
          meta: lessonHours(4, 24),
        },
        {
          eyebrow: t('careerPath.demo.stage.final'),
          title: t('careerPath.demo.ai.stage.final.title'),
          meta: certificate,
          state: 'trophy',
        },
      ],
      metaFoot: t('careerPath.demo.ai.metaFoot'),
      ctaLabel: t('careerPath.demo.cta.continue'),
      ctaIcon: 'play',
    },
    {
      id: 'security',
      icon: 'shield',
      name: t('careerPath.demo.security.name'),
      meta: lessonHours(9, 62),
      tint: '#4a4a4a',
      desc: t('careerPath.demo.security.desc'),
      kpis: [
        { value: '0/9', label: kpiProgress, accent: true },
        { value: months(5), label: kpiDuration },
        { value: '3.4k', label: kpiPeers },
      ],
      stages: [
        {
          eyebrow: t('careerPath.demo.stage.startHere'),
          title: t('careerPath.demo.security.stage.1.title'),
          meta: lessonHours(2, 14),
          state: 'current',
        },
        {
          eyebrow: stage(2),
          title: t('careerPath.demo.security.stage.2.title'),
          meta: lessonHours(2, 12),
        },
        {
          eyebrow: stage(3),
          title: t('careerPath.demo.security.stage.3.title'),
          meta: lessonHours(3, 20),
        },
        {
          eyebrow: stage(4),
          title: t('careerPath.demo.security.stage.4.title'),
          meta: lessonHours(2, 16),
        },
        {
          eyebrow: t('careerPath.demo.stage.final'),
          title: t('careerPath.demo.security.stage.final.title'),
          meta: certificate,
          state: 'trophy',
        },
      ],
      metaFoot: t('careerPath.demo.security.metaFoot'),
      ctaLabel: startCta,
      ctaIcon: 'flag',
    },
    {
      id: 'mobile',
      icon: 'rocket',
      name: t('careerPath.demo.mobile.name'),
      meta: lessonHours(11, 74),
      tint: '#0E7C8E',
      tint2: '#1AA5BC',
      desc: t('careerPath.demo.mobile.desc'),
      kpis: [
        { value: '0/11', label: kpiProgress, accent: true },
        { value: months(6), label: kpiDuration },
        { value: '2.9k', label: kpiPeers },
      ],
      stages: [
        {
          eyebrow: t('careerPath.demo.stage.startHere'),
          title: t('careerPath.demo.mobile.stage.1.title'),
          meta: lessonHours(2, 14),
          state: 'current',
        },
        {
          eyebrow: stage(2),
          title: t('careerPath.demo.mobile.stage.2.title'),
          meta: lessonHours(3, 20),
        },
        {
          eyebrow: stage(3),
          title: t('careerPath.demo.mobile.stage.3.title'),
          meta: lessonHours(3, 18),
        },
        {
          eyebrow: stage(4),
          title: t('careerPath.demo.mobile.stage.4.title'),
          meta: lessonHours(2, 12),
        },
        {
          eyebrow: t('careerPath.demo.stage.final'),
          title: t('careerPath.demo.mobile.stage.final.title'),
          meta: certificate,
          state: 'trophy',
        },
      ],
      metaFoot: t('careerPath.demo.mobile.metaFoot'),
      ctaLabel: startCta,
      ctaIcon: 'flag',
    },
  ];
}

/* --- inline pin / pill glyphs --- */
const Check = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M5 13l4 4L19 7"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const Play = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M8 5.5v13l11-6.5z" />
  </svg>
);
const Lock = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect x="5" y="11" width="14" height="9" rx="2" fill="currentColor" />
    <path
      d="M8 11V8a4 4 0 0 1 8 0v3"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
    />
  </svg>
);
const Trophy = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M7 4h10v4a5 5 0 0 1-10 0V4z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M7 6H4v1a3 3 0 0 0 3 3M17 6h3v1a3 3 0 0 1-3 3M9 20h6M12 13v4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);
const Flag = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M5 21V4M5 4h11l-2 4 2 4H5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const Chevron = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden style={{ flexShrink: 0 }}>
    <path
      d="M9 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function stateGlyph(state: StageState | NodeState | undefined, fallback?: string) {
  switch (state) {
    case 'done':
      return <Check />;
    case 'current':
      return <Play />;
    case 'trophy':
      return <Trophy />;
    case 'locked':
      return fallback != null ? <>{fallback}</> : <Lock />;
    default:
      return fallback != null ? <>{fallback}</> : <Lock />;
  }
}

/** Build the winding road path + per-node coordinates (viewBox 0 0 600 H,
 *  preserveAspectRatio="none" — so node left/top percentages share the
 *  path's coordinate space exactly). */
function buildRoadmap(count: number, perRow: number) {
  const R = Math.max(2, Math.floor(perRow) || 5);
  const rows = Math.max(1, Math.ceil(count / R));
  const W = 600;
  const xL = 60;
  const xR = 540;
  const yTop = 45;
  const rowGap = 90;
  const TURN = 50;
  const H = yTop + (rows - 1) * rowGap + yTop;
  const yOf = (i: number) => yTop + i * rowGap;

  // path
  const seg: string[] = [`M ${xL} ${yOf(0)}`];
  for (let i = 0; i < rows; i++) {
    const y = yOf(i);
    const even = i % 2 === 0;
    const xEnd = even ? xR : xL;
    seg.push(`L ${xEnd} ${y}`);
    if (i < rows - 1) {
      const yN = yOf(i + 1);
      const cpx = even ? xR + TURN : xL - TURN;
      seg.push(`C ${cpx} ${y} ${cpx} ${yN} ${xEnd} ${yN}`);
    }
  }

  // node coordinates (as percentages of the viewBox)
  const coords = Array.from({ length: count }, (_, n) => {
    const i = Math.floor(n / R);
    const k = n % R;
    const even = i % 2 === 0;
    const t = R > 1 ? k / (R - 1) : 0;
    const x = even ? xL + (xR - xL) * t : xR - (xR - xL) * t;
    const y = yOf(i);
    return { leftPct: (x / W) * 100, topPct: (y / H) * 100, topRow: i === 0 };
  });

  return { path: seg.join(' '), coords, rows, viewH: H };
}

export function LearningPathV2({
  careers,
  activeId,
  requireAuth,
  paddingOuter,
  paddingInner,
}: LearningPathV2Props) {
  const { t } = useTranslation('site-builder-blocks');
  const currentUser = useCurrentUser();
  const cls = `lpv2-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const gradId = `${cls}-grad`;

  const defaults = useMemo(() => buildDefaultCareers(t), [t]);
  const list = asList<Career>(careers);
  const careerList = list.length > 0 ? list : defaults;
  const firstId = careerList[0]?.id ?? '0';
  const [active, setActive] = useState<string>(activeId ?? firstId);

  // Auth gate — mirror the shell's requireAuth semantics.
  const isSignedIn = currentUser != null;
  const gate = requireAuth ?? 'any';
  if ((gate === 'in' && !isSignedIn) || (gate === 'out' && isSignedIn)) return <></>;

  if (careerList.length === 0) {
    return (
      <SectionShell paddingOuter={paddingOuter} paddingInner={paddingInner}>
        <EmptyState title={t('careerPath.empty.title')} hint={t('careerPath.empty.hint')} />
      </SectionShell>
    );
  }

  const activeCareer = careerList.find((c, i) => (c.id ?? String(i)) === active) ?? careerList[0];
  if (!activeCareer) return <></>;
  const aTint = activeCareer.tint || '#1AA5BC';
  const aTint2 = activeCareer.tint2 || activeCareer.tint || '#1AA5BC';
  const kpis = asList<CareerKpi>(activeCareer.kpis);
  const pills = asList<StagePill>(activeCareer.stagePills);
  const nodes = asList<RoadNode>(activeCareer.nodes);
  const stages = asList<Stage>(activeCareer.stages);
  const road = nodes.length > 0 ? buildRoadmap(nodes.length, activeCareer.perRow ?? 5) : null;
  const doneNodes = nodes.filter((n) => n.state === 'done').length;
  const roadProgress = nodes.length > 0 ? Math.min(1, doneNodes / nodes.length) : 0;

  return (
    <SectionShell paddingOuter={paddingOuter} paddingInner={paddingInner}>
      <style
        dangerouslySetInnerHTML={{
          __html: css(
            cls,
            ACCENT,
            ACCENT2,
            SURFACE,
            SURFACE2,
            BORDER,
            BORDER_SOFT,
            TEXT,
            MUTED,
            DIM,
          ),
        }}
      />
      <div className={cls}>
        {/* picker */}
        <div className="career-picker">
          {careerList.map((c, i) => {
            const id = c.id ?? String(i);
            const isActive = id === active;
            return (
              <button
                key={id}
                type="button"
                className={`career-tile${isActive ? ' active' : ''}`}
                style={{ ['--tint' as string]: c.tint || ACCENT }}
                onClick={() => setActive(id)}
                aria-pressed={isActive}
              >
                <span className={`career-tile-icon${c.image ? ' has-img' : ''}`}>
                  {c.image ? (
                    <img src={c.image} alt={tx(c.name)} />
                  ) : (
                    <SidebarIcon name={c.icon || 'compass'} size={24} />
                  )}
                </span>
                <div className="career-tile-name">{tx(c.name)}</div>
                {tx(c.meta) && <div className="career-tile-meta">{tx(c.meta)}</div>}
                {tx(c.badge) && <span className="career-tile-popular">{tx(c.badge)}</span>}
              </button>
            );
          })}
        </div>

        {/* detail */}
        <div
          className="career-detail"
          style={{ ['--tint' as string]: aTint, ['--tint-2' as string]: aTint2 }}
        >
          <div className="career-detail-head">
            <span className={`career-detail-icon${activeCareer.image ? ' has-img' : ''}`}>
              {activeCareer.image ? (
                <img src={activeCareer.image} alt={tx(activeCareer.name)} />
              ) : (
                <SidebarIcon name={activeCareer.icon || 'compass'} size={28} />
              )}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="career-detail-title">
                {tx(activeCareer.title) || tx(activeCareer.name)}
              </div>
              {tx(activeCareer.desc) && (
                <div className="career-detail-desc">{tx(activeCareer.desc)}</div>
              )}
            </div>
            {kpis.length > 0 && (
              <div className="career-kpi-grid">
                {kpis.map((k, i) => (
                  <div className="career-kpi" key={i}>
                    <div className={`career-kpi-value${k.accent ? ' accent' : ''}`}>{k.value}</div>
                    <div className="career-kpi-label">{tx(k.label)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* stage-pill banner */}
          {pills.length > 0 && (
            <div className="roadmap-stages">
              {pills.map((p, i) => (
                <span
                  key={`p${i}`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                >
                  <span className={`stage-pill${p.state ? ` ${p.state}` : ''}`}>
                    <span className="stage-pill-ic">{stateGlyph(p.state, '')}</span>
                    {tx(p.label)}
                  </span>
                  {i < pills.length - 1 && (
                    <span className="stage-pill-arrow">
                      <Chevron />
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}
          {/* roadmap OR stage timeline */}
          {road ? (
            <div className="roadmap" style={{ height: `${road.rows * 120}px` }}>
              <svg
                className="roadmap-svg"
                viewBox={`0 0 600 ${road.viewH}`}
                preserveAspectRatio="none"
              >
                <defs>
                  <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor={aTint} />
                    <stop offset="100%" stopColor={aTint2} />
                  </linearGradient>
                </defs>
                <path className="road-bg" d={road.path} />
                <path className="road-bg-dash" d={road.path} />
                <path
                  className="road-fg"
                  pathLength={1}
                  stroke={`url(#${gradId})`}
                  style={{ ['--road-progress' as string]: roadProgress }}
                  d={road.path}
                />
              </svg>
              {nodes.map((n, i) => {
                const co = road.coords[i];
                if (!co) return null;
                const st = n.state;
                const cs = st ? ` ${st}` : '';
                const lbl = co.topRow ? ' lbl-above' : '';
                const Tag = n.href && st !== 'locked' && st !== undefined ? 'a' : 'span';
                return (
                  <Tag
                    key={i}
                    {...(Tag === 'a' ? { href: n.href } : {})}
                    className={`rnode${cs}${lbl}`}
                    style={{ left: `${co.leftPct}%`, top: `${co.topPct}%` }}
                  >
                    <span className="rnode-pin" style={{ ['--tint' as string]: aTint }}>
                      {stateGlyph(
                        st,
                        st === undefined || st === 'locked' ? String(i + 1) : undefined,
                      )}
                    </span>
                    <span className="rnode-label">{tx(n.label)}</span>
                  </Tag>
                );
              })}
            </div>
          ) : stages.length > 0 ? (
            <ol className="stage-list">
              {stages.map((s, i) => (
                <li
                  key={i}
                  className={`stage${s.state ? ` ${s.state === 'trophy' ? 'stage-trophy' : s.state}` : ''}`}
                >
                  <span className="stage-num">{s.state === 'trophy' ? <Trophy /> : i + 1}</span>
                  <div className="stage-body">
                    <div className="stage-head">
                      <div>
                        {tx(s.eyebrow) && <div className="stage-eyebrow">{tx(s.eyebrow)}</div>}
                        <div className="stage-title">{tx(s.title)}</div>
                      </div>
                      {s.meta && <span className="stage-meta">{s.meta}</span>}
                    </div>
                    {tx(s.desc) && <p className="stage-desc">{tx(s.desc)}</p>}
                    {asList<StageCourse>(s.courses).length > 0 && (
                      <ul className="stage-courses">
                        {asList<StageCourse>(s.courses).map((c, ci) => (
                          <li key={ci} className={`stage-course${c.done ? ' done' : ''}`}>
                            <span className="stage-course-check">
                              {c.done ? <Check /> : <Flag />}
                            </span>
                            <span className="stage-course-name">
                              <strong>{tx(c.name)}</strong>
                            </span>
                            {c.dur && <span className="stage-course-dur">{c.dur}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          ) : null}
          {/* CTA row */}
          {(tx(activeCareer.metaFoot) || tx(activeCareer.ctaLabel)) && (
            <div className="career-cta-row">
              {tx(activeCareer.metaFoot) && (
                <div className="career-meta-foot">{tx(activeCareer.metaFoot)}</div>
              )}
              {tx(activeCareer.ctaLabel) && (
                <Button href={activeCareer.ctaHref}>
                  {activeCareer.ctaIcon === 'flag' ? <Flag /> : <Play />}
                  {tx(activeCareer.ctaLabel)}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </SectionShell>
  );
}

/** Scoped stylesheet — ported from the BTK reference, design-token-ized. */
function css(
  c: string,
  accent: string,
  accent2: string,
  surface: string,
  surface2: string,
  border: string,
  borderSoft: string,
  text: string,
  muted: string,
  dim: string,
): string {
  return `
.${c}{position:relative}
.${c} .career-picker{display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:10px}
.${c} .career-tile{background:${surface2};border:1.5px solid ${border};border-radius:12px;padding:14px 12px;
  cursor:pointer;display:flex;flex-direction:column;align-items:center;gap:8px;text-align:center;
  transition:all .25s ease;position:relative;color:${text};font:inherit}
.${c} .career-tile:hover{transform:translateY(-2px);border-color:${accent}}
.${c} .career-tile.active{border-color:${accent};box-shadow:0 8px 24px -6px color-mix(in srgb,${accent} 30%,transparent);
  background:color-mix(in srgb,${accent} 14%,${surface2})}
.${c} .career-tile.active::after{content:"";position:absolute;bottom:-10px;left:50%;width:14px;height:14px;
  background:color-mix(in srgb,${accent} 14%,${surface2});border-right:1.5px solid ${accent};
  border-bottom:1.5px solid ${accent};transform:translateX(-50%) rotate(45deg);z-index:3}
.${c} .career-tile-icon{width:44px;height:44px;border-radius:12px;display:inline-flex;align-items:center;
  justify-content:center;background:color-mix(in srgb,var(--tint,${accent}) 16%,transparent);color:var(--tint,${accent});flex-shrink:0;overflow:hidden}
.${c} .career-tile-icon.has-img{background:${surface}}
.${c} .career-tile-icon img{width:100%;height:100%;object-fit:cover;display:block}
.${c} .career-tile-name{font-size:13px;font-weight:600;line-height:1.2;letter-spacing:-.01em}
.${c} .career-tile-meta{font-size:10px;color:${dim};letter-spacing:.03em}
.${c} .career-tile-popular{position:absolute;top:8px;right:8px;font-size:9px;font-weight:600;padding:2px 7px;
  border-radius:999px;background:linear-gradient(135deg,#4a4a4a,#374151);color:#fff;letter-spacing:.04em}
.${c} .career-detail{margin-top:24px;background:${surface2};border:1px solid ${border};border-radius:16px;padding:22px 24px;position:relative}
.${c} .career-detail-head{display:flex;align-items:flex-start;gap:16px;flex-wrap:wrap;padding-bottom:18px;border-bottom:1px solid ${border}}
.${c} .career-detail-icon{width:56px;height:56px;border-radius:14px;display:inline-flex;align-items:center;
  justify-content:center;background:linear-gradient(135deg,var(--tint,${accent}),var(--tint-2,${accent}));color:#fff;flex-shrink:0;overflow:hidden;
  box-shadow:0 6px 18px -4px color-mix(in srgb,var(--tint,${accent}) 50%,transparent)}
.${c} .career-detail-icon img{width:100%;height:100%;object-fit:cover;display:block}
.${c} .career-detail-title{font-size:22px;font-weight:600;letter-spacing:-.02em;line-height:1.2;color:${text}}
.${c} .career-detail-desc{font-size:13px;color:${muted};margin-top:4px;line-height:1.5;max-width:60ch}
.${c} .career-kpi-grid{display:flex;gap:14px;flex-wrap:wrap;margin-left:auto}
.${c} .career-kpi{padding:10px 14px;background:${surface};border:1px solid ${border};border-radius:10px;min-width:100px}
.${c} .career-kpi-value{font-size:17px;font-weight:600;letter-spacing:-.01em;line-height:1;color:${text}}
.${c} .career-kpi-value.accent{color:${accent}}
.${c} .career-kpi-label{font-size:10.5px;color:${dim};letter-spacing:.04em;text-transform:uppercase;margin-top:4px;line-height:1}
.${c} .stage-list{display:flex;flex-direction:column;gap:14px;margin-top:20px;position:relative}
.${c} .stage-list::before{content:"";position:absolute;left:20px;top:30px;bottom:30px;width:2px;
  background:linear-gradient(180deg,${accent},${accent2});opacity:.35;border-radius:999px}
.${c} .stage{display:grid;grid-template-columns:42px 1fr;gap:16px;align-items:flex-start}
.${c} .stage-num{width:42px;height:42px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;
  background:${surface};border:2px solid ${border};font-size:13px;font-weight:700;color:${dim};flex-shrink:0;position:relative;z-index:2}
.${c} .stage.done .stage-num{background:linear-gradient(135deg,${accent},${accent2});border-color:${accent};color:#fff;
  box-shadow:0 4px 12px -2px color-mix(in srgb,${accent} 40%,transparent)}
.${c} .stage.current .stage-num{background:${surface};border-color:${accent};color:${accent};
  box-shadow:0 0 0 4px color-mix(in srgb,${accent} 18%,transparent);animation:${c}-pulse 2.4s ease-out infinite}
@keyframes ${c}-pulse{0%,100%{box-shadow:0 0 0 4px color-mix(in srgb,${accent} 18%,transparent)}50%{box-shadow:0 0 0 10px transparent}}
.${c} .stage-trophy .stage-num{background:linear-gradient(135deg,${accent2},#4a4a4a);border-color:#4a4a4a;color:#fff;box-shadow:0 4px 14px rgba(74,74,74,.4)}
.${c} .stage-body{background:${surface};border:1px solid ${border};border-radius:12px;padding:14px 16px;transition:border-color .2s ease}
.${c} .stage.current .stage-body{border-color:${accent}}
.${c} .stage-head{display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap}
.${c} .stage-title{font-size:14px;font-weight:600;letter-spacing:-.01em;color:${text}}
.${c} .stage-eyebrow{font-size:10px;font-weight:600;letter-spacing:.12em;text-transform:uppercase;color:${dim}}
.${c} .stage.current .stage-eyebrow{color:${accent}}
.${c} .stage.done .stage-eyebrow{color:${accent}}
.${c} .stage-meta{font-size:11px;color:${dim}}
.${c} .stage-desc{font-size:12px;color:${muted};margin-top:4px;line-height:1.5}
.${c} .stage-courses{list-style:none;padding:0;margin:12px 0 0;display:flex;flex-direction:column;gap:6px}
.${c} .stage-course{display:flex;align-items:center;gap:10px;padding:8px 10px;background:${surface2};
  border:1px solid ${borderSoft};border-radius:8px;font-size:12px;transition:background .15s ease}
.${c} .stage-course-check{width:18px;height:18px;border-radius:50%;display:inline-flex;align-items:center;
  justify-content:center;background:${surface};border:1.5px solid ${border};color:${dim};flex-shrink:0}
.${c} .stage-course.done .stage-course-check{background:${accent};border-color:${accent};color:#fff}
.${c} .stage-course-name{flex:1;min-width:0}
.${c} .stage-course-name strong{font-weight:500;color:${text}}
.${c} .stage-course.done .stage-course-name strong{color:${muted};text-decoration:line-through}
.${c} .stage-course-dur{font-size:10.5px;color:${dim};flex-shrink:0}
.${c} .career-cta-row{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:22px;flex-wrap:wrap}
.${c} .career-resume-btn{display:inline-flex;align-items:center;gap:8px;padding:0 18px;height:42px;border-radius:10px;
  background:linear-gradient(135deg,${accent},${accent});color:#fff;font-size:13px;font-weight:600;cursor:pointer;border:none;
  text-decoration:none;box-shadow:0 6px 18px -4px color-mix(in srgb,${accent} 40%,transparent),inset 0 1px 0 rgba(255,255,255,.2);
  transition:transform .15s ease,box-shadow .2s ease}
.${c} .career-resume-btn:hover{transform:translateY(-1px)}
.${c} .career-meta-foot{font-size:11.5px;color:${muted};display:inline-flex;align-items:center;gap:8px;flex-wrap:wrap}
.${c} .career-meta-foot strong{color:${text};font-weight:600}
.${c} .roadmap{position:relative;margin-top:20px;padding:30px 14px 20px;
  background:radial-gradient(40% 60% at 50% 50%,color-mix(in srgb,${accent} 6%,transparent),transparent 70%),${surface};
  border:1px solid ${border};border-radius:14px}
.${c} .roadmap-svg{position:absolute;inset:0;width:100%;height:100%;z-index:0}
.${c} .roadmap-svg .road-bg{fill:none;stroke:color-mix(in srgb,${border} 80%,transparent);stroke-width:16;stroke-linecap:round;stroke-linejoin:round}
.${c} .roadmap-svg .road-bg-dash{fill:none;stroke:rgba(148,163,184,.4);stroke-width:1.5;stroke-dasharray:6 8}
.${c} .roadmap-svg .road-fg{fill:none;stroke-width:16;stroke-linecap:round;stroke-linejoin:round;
  stroke-dasharray:1;stroke-dashoffset:calc(1 - var(--road-progress,0))}
.${c} .rnode{position:absolute;transform:translate(-50%,-50%);display:flex;flex-direction:column;align-items:center;
  gap:6px;z-index:2;cursor:pointer;text-decoration:none}
.${c} .rnode-pin{width:36px;height:36px;border-radius:50%;background:${surface};border:2.5px solid ${border};color:${dim};
  display:inline-flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;transition:all .2s ease;box-shadow:0 4px 10px rgba(0,0,0,.18)}
.${c} .rnode:hover .rnode-pin{transform:scale(1.08)}
.${c} .rnode.done .rnode-pin{background:linear-gradient(135deg,var(--tint,${accent}),var(--tint,${accent}));border-color:transparent;color:#fff}
.${c} .rnode.current .rnode-pin{background:${surface};border-color:${accent};color:${accent};width:44px;height:44px;
  box-shadow:0 0 0 5px color-mix(in srgb,${accent} 22%,transparent),0 6px 14px rgba(0,0,0,.2);animation:${c}-rpulse 2.2s ease-out infinite}
@keyframes ${c}-rpulse{0%,100%{box-shadow:0 0 0 5px color-mix(in srgb,${accent} 22%,transparent),0 6px 14px rgba(0,0,0,.2)}50%{box-shadow:0 0 0 11px transparent,0 6px 14px rgba(0,0,0,.2)}}
.${c} .rnode.trophy .rnode-pin{background:linear-gradient(135deg,${accent2},#4a4a4a);border-color:transparent;color:#fff;width:44px;height:44px;box-shadow:0 0 18px rgba(74,74,74,.45),0 4px 12px rgba(0,0,0,.18)}
.${c} .rnode-label{font-size:10.5px;color:${muted};max-width:90px;text-align:center;line-height:1.25;font-weight:500;
  background:${surface};padding:2px 6px;border-radius:4px;pointer-events:none;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.${c} .rnode.done .rnode-label{color:${text}}
.${c} .rnode.current .rnode-label{color:#fff;font-weight:600;background:linear-gradient(135deg,${accent},${accent})}
.${c} .rnode.trophy .rnode-label{color:#fff;font-weight:700;background:linear-gradient(135deg,${accent2},#4a4a4a)}
.${c} .rnode.lbl-above .rnode-label{order:-1}
.${c} .roadmap-stages{display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:14px;margin-top:20px}
.${c} .stage-pill{display:inline-flex;align-items:center;gap:6px;font-size:11px;font-weight:500;padding:4px 10px;
  background:${surface2};border:1px solid ${border};border-radius:999px;color:${muted}}
.${c} .stage-pill-ic{display:inline-flex}
.${c} .stage-pill.done{color:${accent};border-color:color-mix(in srgb,${accent} 40%,transparent);background:color-mix(in srgb,${accent} 8%,transparent)}
.${c} .stage-pill.current{color:${accent};border-color:${accent};background:color-mix(in srgb,${accent} 10%,transparent)}
.${c} .stage-pill.trophy{color:${accent2}}
.${c} .stage-pill-arrow{color:${dim};display:inline-flex}
`;
}
